import { useLocation, useParams } from "react-router";
import { useMemo } from "react";

import {
  buildScopedUrl,
  getScopeFromPathname,
  getScopeId,
  type AppScope,
} from "../utils/scopeRouting";

export interface ScopeParams {
  scope: AppScope;
  id: string;
  buildUrl: (path?: string) => string;
  [key: string]: any;
}

/**
 * Centralized hook for scope-based routing.
 *
 * Returns scope, id, all other route params, AND a buildUrl function
 * that already has scope/id baked in.
 *
 * @example
 * // URL: /trial/NCT123/subjects/overview/SUBJ-001
 * const { scope, id, subjectId, buildUrl } = useScopeParams();
 * // scope = "trial"
 * // id = "NCT123"
 * // subjectId = "SUBJ-001"
 * // buildUrl('home') => "/trial/NCT123/home"
 * // buildUrl('subjects/overview') => "/trial/NCT123/subjects/overview"
 */
export function useScopeParams(): ScopeParams {
  const params = useParams();
  const location = useLocation();

  const scope = getScopeFromPathname(location.pathname, params.scope);
  const id = getScopeId(scope, params.id, location.pathname);
  const globalBase =
    scope === "global"
      ? location.pathname.split("/").filter(Boolean)[0] || ""
      : "";

  const buildUrl = useMemo(
    () =>
      (path: string = "") =>
        buildScopedUrl(scope, id, path, globalBase),
    [scope, id, globalBase],
  );

  return {
    scope,
    id,
    buildUrl,
    ...params,
  };
}
